import { useCallback, useContext } from 'react'
import { AxiosContext } from '../context/request.context'
import position from '../components/position/position'
const qs = require('qs')

const COUNT_OF_DAYS_OF_CLOSED_POSITIONS = 30

function useDashbord() {
  const { authRequest } = useContext(AxiosContext)

  const getDashbord = useCallback(
    async ({ stage, page, filter, sort, pageSize, signal }) => {
      if (!sort.path) {
        sort = sort._default
      }

      let options = {
        filters: {
          ...filter,
          isCurrentStage: true
        }
      }

      if (options.filters.user) {
        const userIds = options.filters.user.id['$in']
        delete options.filters.user
        options.filters = {
          ...options.filters,
          $or: [
            {
              user: {
                id: {
                  $in: userIds
                }
              }
            },
            {
              position: {
                drawings: {
                  user: {
                    id: {
                      $in: userIds
                    }
                  }
                }
              }
            }
          ]
        }
      }

      if (options.filters.status) {
        const statusIds = options.filters.status.id['$in']
        delete options.filters.status
        options.filters = {
          ...options.filters,
          $or: [
            {
              status: {
                id: {
                  $in: statusIds
                }
              }
            },
            {
              position: {
                drawings: {
                  status: {
                    id: {
                      $in: statusIds
                    }
                  }
                }
              }
            }
          ]
        }
      }


      if (options.filters.position?.drawings?.groupCode) {
        const groupCodes = options.filters.position.drawings.groupCode['$in'] || []
        const hasNoPrint = groupCodes.includes('__NO_PRINT__')
        const printMethods = groupCodes.filter(code => code !== '__NO_PRINT__')
        
        // Сохраняем другие фильтры position (без drawings)
        const otherPositionFilters = { ...options.filters.position }
        delete otherPositionFilters.drawings
        
        // Сохраняем другие фильтры на корневом уровне
        const otherFilters = { ...options.filters }
        delete otherFilters.position
        
        // Удаляем старый фильтр drawings
        delete options.filters.position
        
        if (hasNoPrint) {
          // Если выбран только "Без печати" (printMethods.length всегда будет 0 из-за новой логики)
          const noDrawingsFilter = {
            position: {
              ...otherPositionFilters,
              drawings: {
                id: {
                  $null: true
                }
              }
            }
          }
          
          // Объединяем с другими фильтрами
          if (Object.keys(otherFilters).length > 0) {
            const hasComplexFilters = otherFilters.$or || otherFilters.$and
            if (hasComplexFilters) {
              options.filters = {
                $and: [
                  otherFilters,
                  noDrawingsFilter
                ]
              }
            } else {
              options.filters = {
                ...otherFilters,
                ...noDrawingsFilter
              }
            }
          } else {
            options.filters = noDrawingsFilter
          }
        } else if (printMethods.length > 0) {
          // Если выбраны только обычные методы печати
          options.filters.position = {
            ...otherPositionFilters,
            drawings: {
              groupCode: {
                $in: printMethods
              }
            }
          }
          // Восстанавливаем otherFilters
          if (Object.keys(otherFilters).length > 0) {
            options.filters = {
              ...otherFilters,
              ...options.filters
            }
          }
        }
      }

      if (stage) {
        options.filters.stage = {
          id: stage
        }
      }

      if (!stage) {
        const current = { ...options.filters }
        const closedPositionsFilterDate = new Date()
        closedPositionsFilterDate.setDate(closedPositionsFilterDate.getDate() - COUNT_OF_DAYS_OF_CLOSED_POSITIONS)
        options.filters = {
          $or: [
            {
              $and: [
                {
                  ...current
                },
                {
                  updatedAt: {
                    $gte: closedPositionsFilterDate.toISOString()
                  },
                  stage: {
                    endStage: true
                  }
                }
              ]
            },
            {
              $and: [
                {
                  ...current
                },
                {
                  stage: {
                    endStage: false,
                    //TODO STAGE HIDE FILTER
                    isHideStage: false
                  }
                }
              ]
            }
          ]
        }
      }

      const query = qs.stringify(
        {
          populate: {
            stage: true,
            status: true,
            user: true,
            comments: {
              sort: ['createdAt:desc']
            },
            position: {
              populate: {
                drawings: {
                  populate: {
                    status: true,
                    user: true,
                    subusers: {
                      populate: {
                        user: true
                      },
                      sort: ['countChangeDate:desc']
                    }
                  }
                },
                order: {
                  populate: {
                    userinfo: true,
                    localComment: true,
                    dontDelay: true
                  }
                },
                custom_works: {
                  populate: '*'
                }
              }
            }
          },
          pagination: {
            page,
            pageSize
          },
          ...options,
          sort
        },
        {
          encodeValuesOnly: true // prettify URL
        }
      )

      return await authRequest({
        url: 'c-position-stages?' + query,
        signal
      })
    },
    [authRequest]
  )

  const getPrintLoading = useCallback(
    async date => {
      return await authRequest({
        url: `positionss/getPrintLoading?date=${date}`,
        method: 'GET'
      })
    },
    [authRequest]
  )

  const getNotifications = useCallback(async () => {
    return await authRequest({
      url: `notifications`,
      method: 'GET'
    })
  }, [authRequest])

  const getDocsLink = useCallback(async () => {
    const query = qs.stringify(
      {
        fields: ['link']
      },
      {
        encodeValuesOnly: true
      }
    )

    return await authRequest({
      url: `doc-link?` + query,
      method: 'GET'
    })
  }, [authRequest])

  const getPrintGroups = useCallback(async () => {
    const query = qs.stringify(
      {
        filters: {
          $or: [
            { isBasicWorkGroup: { $eq: false } },
            { isBasicWorkGroup: { $null: true } }
          ]
        },
        populate: {
          print_lists: true
        },
        pagination: {
          pageSize: 100
        }
      })
    return await authRequest({
      url: `/print-groups?${query}`,
      method: 'GET'
    })
  }, [authRequest])

  const getPrintsCount = useCallback(async () => {
    return await authRequest({
      url: `positionss/getPrintsCount`,
      method: 'GET'
    })
  }, [authRequest])

  const getRemoteStocks = useCallback(async () => {
    const query = qs.stringify(
      {
        fields: ['stockId', 'stockName'],
        pagination: {
          pageSize: 200
        }
      },
      {
        encodeValuesOnly: true
      }
    )

    return await authRequest({
      url: `remote-stocks?` + query,
      method: 'GET'
    })
  }, [authRequest])

  return {
    getDashbord,
    getPrintLoading,
    getDocsLink,
    getNotifications,
    getPrintGroups,
    getPrintsCount,
    getRemoteStocks
  }
}

export default useDashbord
